const RGB_COLOR_REGEX =
  /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i;

const clampChannel = (value: number) => Math.max(0, Math.min(255, value));

const channelToHex = (value: number) =>
  clampChannel(value).toString(16).padStart(2, '0').toUpperCase();

const rgbStringToHex = (rgbValue: string): string | null => {
  const match = rgbValue.match(RGB_COLOR_REGEX);
  if (!match) {
    return null;
  }

  const [red, green, blue] = match.slice(1).map((value) => Number(value));
  if ([red, green, blue].some((value) => Number.isNaN(value) || value > 255)) {
    return null;
  }

  return `#${channelToHex(red)}${channelToHex(green)}${channelToHex(blue)}`;
};

const getComputedColor = (value: string): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const probe = document.createElement('span');
  probe.style.color = '';
  probe.style.color = value.trim();

  if (!probe.style.color) {
    return null;
  }

  probe.style.display = 'none';
  document.body.appendChild(probe);
  const computedColor = window.getComputedStyle(probe).color;
  document.body.removeChild(probe);
  return computedColor || null;
};

const hexToRgb = (hexValue: string): [number, number, number] | null => {
  const normalizedHex = hexValue.trim().replace('#', '');
  if (!/^[0-9A-Fa-f]{6}$/.test(normalizedHex)) {
    return null;
  }

  const red = parseInt(normalizedHex.slice(0, 2), 16);
  const green = parseInt(normalizedHex.slice(2, 4), 16);
  const blue = parseInt(normalizedHex.slice(4, 6), 16);
  return [red, green, blue];
};

export const normalizeColorToHex = (value: string): string | null => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  const computedColor = getComputedColor(trimmedValue);
  if (!computedColor) {
    return null;
  }

  return rgbStringToHex(computedColor);
};

export const hexToRgbString = (hexValue: string): string | null => {
  const rgb = hexToRgb(hexValue);
  if (!rgb) {
    return null;
  }

  const [red, green, blue] = rgb;
  return `rgb(${red}, ${green}, ${blue})`;
};

export const hexToHslString = (hexValue: string): string | null => {
  const rgb = hexToRgb(hexValue);
  if (!rgb) {
    return null;
  }

  const [red, green, blue] = rgb.map((value) => value / 255);
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;

  let hue = 0;
  const lightness = (max + min) / 2;
  let saturation = 0;

  if (delta !== 0) {
    saturation =
      lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

    switch (max) {
      case red:
        hue = (green - blue) / delta + (green < blue ? 6 : 0);
        break;
      case green:
        hue = (blue - red) / delta + 2;
        break;
      default:
        hue = (red - green) / delta + 4;
        break;
    }

    hue /= 6;
  }

  const roundedHue = Math.round(hue * 360);
  const roundedSaturation = Math.round(saturation * 100);
  const roundedLightness = Math.round(lightness * 100);
  return `hsl(${roundedHue}, ${roundedSaturation}%, ${roundedLightness}%)`;
};

export const isValidBrandColor = (value: string): boolean => {
  return normalizeColorToHex(value) !== null;
};
